import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { getCustomerPortalSetupRequirements } from '../api/setupProfilesApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { useCustomerPortal } from '../portal/CustomerPortalContext';
import { formatCapabilityCode, formatModuleLabel } from '../utils/customerPortalLabels';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';

const { semantic } = colorTokens;

function setupItemLabel(item) {
  if (item?.item_code) {
    return formatCapabilityCode(item.item_code);
  }
  if (item?.title && /_/.test(item.title)) {
    return formatCapabilityCode(item.title);
  }
  if (item?.title && item.title === item.title.toLowerCase() && item.title.includes(' ')) {
    return formatCapabilityCode(item.title.replace(/\s+/g, '_'));
  }
  return item?.title || formatModuleLabel(item?.module_code);
}

function SetupItemsList({ items, asCapability = false }) {
  if (!items?.length) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {items.map((item) => {
        const label = asCapability
          ? setupItemLabel(item)
          : (item.title && !/_/.test(item.title)
            ? item.title
            : formatModuleLabel(item.title, item.module_code));

        return (
          <Box
            key={`${item.profile_code}-${item.item_code || item.title}`}
            sx={{
              py: 1.25,
              px: 1.5,
              bgcolor: semantic.bg.input,
              border: `1px solid ${semantic.border.subtle}`,
              borderRadius: 1,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: typographyTokens.fontFamily,
                fontWeight: typographyTokens.fontWeightSemibold,
                fontSize: 13,
                lineHeight: '18px',
                color: semantic.text.primary,
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function SectionHeading({ children }) {
  return (
    <Typography
      sx={{
        mb: 1.5,
        fontSize: 12,
        lineHeight: '16px',
        letterSpacing: '0.24px',
        color: semantic.text.muted,
        textTransform: 'uppercase',
        fontWeight: typographyTokens.fontWeightSemibold,
      }}
    >
      {children}
    </Typography>
  );
}

export function CustomerSetupRequirementsBlock({ tenantId }) {
  const { t } = useTranslation(['setupProfiles', 'common', 'customerPortal']);
  const { activeCustomerId } = useCustomerPortal();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!activeCustomerId || !tenantId) {
      return;
    }
    setLoading(true);
    try {
      const response = await getCustomerPortalSetupRequirements(activeCustomerId, tenantId);
      setData(response);
    } finally {
      setLoading(false);
    }
  }, [activeCustomerId, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <LoadingSkeleton type="Detail" sx={{ mb: 2.5 }} />;
  }

  if (!data) {
    return null;
  }

  const hasGlobal = Boolean(data.global_setup?.length);
  const hasModuleRequired = Boolean(data.enabled_module_setup?.length);
  const hasOptional = Boolean(data.optional_setup?.length);

  if (!hasGlobal && !hasModuleRequired && !hasOptional) {
    return null;
  }

  return (
    <SectionCard
      variant="Default"
      title={t('setupProfiles:accountSetupTitle')}
      subtitle={t('setupProfiles:customerSafeSummary')}
      sx={{ mb: 2.5 }}
    >
      {hasGlobal && (
        <Box sx={{ mb: hasModuleRequired || hasOptional ? 2.5 : 0 }}>
          <SectionHeading>{t('setupProfiles:globalRequired')}</SectionHeading>
          <SetupItemsList items={data.global_setup} />
        </Box>
      )}

      {hasModuleRequired && (
        <Box sx={{ mb: hasOptional ? 2.5 : 0 }}>
          <SectionHeading>{t('setupProfiles:moduleRequired')}</SectionHeading>
          <SetupItemsList items={data.enabled_module_setup} />
        </Box>
      )}

      {hasOptional && (
        <Box>
          <SectionHeading>{t('setupProfiles:moduleOptional')}</SectionHeading>
          <SetupItemsList items={data.optional_setup} asCapability />
        </Box>
      )}
    </SectionCard>
  );
}
